import React, { useState } from 'react';
import { Calendar, Clock, CheckCircle, AlertTriangle, Bell, Plus } from 'lucide-react';

export const ComplianceCalendar = () => {
    const [currentView, setCurrentView] = useState('month');
    const [selectedDate, setSelectedDate] = useState(new Date());

    const complianceEvents = [
        {
            id: '1',
            title: 'GSTR-1 Filing',
            client: 'ABC Industries',
            type: 'GSTR-1',
            dueDate: '2024-01-20',
            status: 'Pending',
            priority: 'High',
            assignee: 'John Doe',
            description: 'Monthly GSTR-1 return filing for December 2023'
        },
        {
            id: '2',
            title: 'GSTR-3B Filing',
            client: 'XYZ Corp',
            type: 'GSTR-3B',
            dueDate: '2024-01-20',
            status: 'In Progress',
            priority: 'High',
            assignee: 'Jane Smith',
            description: 'Monthly GSTR-3B return filing for December 2023'
        },
        {
            id: '3',
            title: 'TDS Return',
            client: 'Tech Solutions',
            type: 'TDS',
            dueDate: '2024-01-31',
            status: 'Pending',
            priority: 'Medium',
            assignee: 'Mike Johnson',
            description: 'Quarterly TDS return filing'
        }
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return 'bg-green-100 text-green-800 border-green-200';
            case 'In Progress': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'Pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'Overdue': return 'bg-red-100 text-red-800 border-red-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'High': return 'border-l-red-500';
            case 'Medium': return 'border-l-yellow-500';
            case 'Low': return 'border-l-green-500';
            default: return 'border-l-gray-500';
        }
    };

    const upcomingDeadlines = complianceEvents
        .filter(event => new Date(event.dueDate) >= new Date())
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
        .slice(0, 5);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Compliance Calendar</h1>
                    <div className="flex space-x-3">
                        <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2">
                            <Bell className="h-4 w-4" />
                            <span>Set Reminders</span>
                        </button>
                        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                            <Plus className="h-4 w-4" />
                            <span>Add Event</span>
                        </button>
                    </div>
                </div>

                {/* View Toggle */}
                <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                        {['month', 'week', 'timeline'].map((view) => (
                            <button
                                key={view}
                                onClick={() => setCurrentView(view)}
                                className={`px-4 py-2 rounded-lg capitalize transition-colors ${currentView === view
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                {view}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center space-x-4">
                        <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                            <option value="">All Clients</option>
                            <option value="abc">ABC Industries</option>
                            <option value="xyz">XYZ Corp</option>
                            <option value="tech">Tech Solutions</option>
                        </select>
                        <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                            <option value="">All Types</option>
                            <option value="gstr1">GSTR-1</option>
                            <option value="gstr3b">GSTR-3B</option>
                            <option value="tds">TDS</option>
                            <option value="audit">Audit</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Due This Week</p>
                            <p className="text-2xl font-bold text-red-600">8</p>
                        </div>
                        <AlertTriangle className="h-8 w-8 text-red-500" />
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">In Progress</p>
                            <p className="text-2xl font-bold text-blue-600">12</p>
                        </div>
                        <Clock className="h-8 w-8 text-blue-500" />
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Completed</p>
                            <p className="text-2xl font-bold text-green-600">45</p>
                        </div>
                        <CheckCircle className="h-8 w-8 text-green-500" />
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Compliance Rate</p>
                            <p className="text-2xl font-bold text-purple-600">94%</p>
                        </div>
                        <Calendar className="h-8 w-8 text-purple-500" />
                    </div>
                </div>
            </div>

            {/* Main View + Deadlines */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Calendar / Timeline */}
                <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        {currentView === 'timeline' ? 'Compliance Timeline' : 'Calendar View'}
                    </h3>

                    {currentView === 'timeline' ? (
                        <div className="space-y-4">
                            {complianceEvents.map((event) => (
                                <div
                                    key={event.id}
                                    className={`border-l-4 ${getPriorityColor(event.priority)} bg-gray-50 p-4 rounded-r-lg`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <h4 className="font-medium text-gray-900">{event.title}</h4>
                                            <p className="text-sm text-gray-600">{event.client}</p>
                                            <p className="text-sm text-gray-500">{event.description}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm font-medium text-gray-900">{event.dueDate}</p>
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(event.status)}`}>
                                                {event.status}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-2 flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Assigned to: {event.assignee}</span>
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${event.priority === 'High' ? 'bg-red-100 text-red-800' :
                                            event.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-green-100 text-green-800'
                                            }`}>
                                            {event.priority} Priority
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="bg-gray-50 rounded-lg p-4 text-center">
                            <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600">Interactive calendar view would be implemented here</p>
                            <p className="text-sm text-gray-500">Showing events for {currentView} view</p>
                        </div>
                    )}
                </div>

                {/* Upcoming Deadlines */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Deadlines</h3>
                    <div className="space-y-3">
                        {upcomingDeadlines.map((event) => (
                            <div key={event.id} className="border border-gray-200 rounded-lg p-3">
                                <div className="flex items-start justify-between mb-2">
                                    <h4 className="font-medium text-gray-900 text-sm">{event.title}</h4>
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(event.status)}`}>
                                        {event.status}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-600">{event.client}</p>
                                <div className="flex items-center justify-between mt-2">
                                    <span className="text-xs text-gray-500">{event.dueDate}</span>
                                    <span className={`text-xs px-2 py-1 rounded-full ${event.priority === 'High' ? 'bg-red-100 text-red-800' :
                                        event.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-green-100 text-green-800'
                                        }`}>
                                        {event.priority}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Reminder Settings */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Automated Reminder Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Email Reminders */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Email Reminders</h4>
                        <div className="space-y-2">
                            <label className="flex items-center">
                                <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                <span className="ml-2 text-sm text-gray-700">7 days before due date</span>
                            </label>
                            <label className="flex items-center">
                                <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                <span className="ml-2 text-sm text-gray-700">3 days before due date</span>
                            </label>
                            <label className="flex items-center">
                                <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                <span className="ml-2 text-sm text-gray-700">On due date</span>
                            </label>
                        </div>
                    </div>

                    {/* SMS Notifications */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">SMS Notifications</h4>
                        <div className="space-y-2">
                            <label className="flex items-center">
                                <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                <span className="ml-2 text-sm text-gray-700">High priority only</span>
                            </label>
                            <label className="flex items-center">
                                <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                <span className="ml-2 text-sm text-gray-700">1 day before due date</span>
                            </label>
                            <label className="flex items-center">
                                <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                <span className="ml-2 text-sm text-gray-700">Overdue notifications</span>
                            </label>
                        </div>
                    </div>

                    {/* Client Notifications */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Client Notifications</h4>
                        <div className="space-y-2">
                            <label className="flex items-center">
                                <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                <span className="ml-2 text-sm text-gray-700">Auto-notify clients</span>
                            </label>
                            <label className="flex items-center">
                                <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                <span className="ml-2 text-sm text-gray-700">Include portal links</span>
                            </label>
                            <label className="flex items-center">
                                <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                <span className="ml-2 text-sm text-gray-700">Document requests</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
